import { action, observable, runInAction } from 'mobx'

import { StoreExt } from '@utils/reactExt'
import {
  IAttendance,
  IBettingRules,
  IClubPortals,
  ICouponRegisterInfo,
  IEventRewardInfo,
  IGameBanners,
  IGiftbox,
  IGiftBoxGameBanners,
  IHotMatch,
  ILiveScore,
  IMyInfo,
  INotice,
  INoticeCreate,
  INotices,
  IOpenGiftbox,
  IRegistrationCommunicationBlock,
  ISportIssueList,
  Schema$ComponentBettingCall,
  Schema$ComponentBettingParamMatch,
} from '@protocol/component'
import { authStore, clubStore, globalStore, matchupStore } from '@store/index'
import {
  BET_CALL_TYPE,
  BET_MONEY_TYPE,
  BET_TYPE_ON,
  CLUB_MEMBER_STATE,
  CLUB_MEMBERSHIP_ID,
  CLUB_STATE,
  EXTRA_ODDS_ITEM_PROPERTY,
  MATCHUP_CATEGORY,
  PROPERTY_ID,
} from 'adv-protocol/src/GameCommon'
import * as _ from 'lodash'
import { CLUB_JOIN_STATE, RATIO_TICKET } from '@constants/index'
import { formatNumber, makeComponentMatchTeamName, makeSelectedOutcome, showToast, TOAST_TYPE } from '@utils/utils'
import { getErrorText, getErrorTextByBetMoneyType, getMixMaxBetError } from '@game/errtext'
import {
  ChampPokerDeepLink,
  ForefrontPopup,
  FriendInvitationInvitor,
  Notice,
  Schema$ClubInfo,
  Schema$ClubOption,
  Schema$CommunicationBlockUserInfo,
  Schema$ExtraOddsItemDefDetail,
  Schema$GameBanner,
  Schema$GameHomeMainBanner,
  Schema$RateFailBetFold,
  Schema$RegistrationSourceDef,
  UrgencyMessage,
  UserExtraOddsItem,
} from '@interface/common'
import { ERR } from 'adv-protocol/src/ErrorCodes'
import { G_ICON, getClubMinMaxBet } from '@game/util'
import { ChangeRateData, OutcomeTrack } from '@store/matchupStore'
import { BubbleTextType, BubbleType } from '@store/globalStore'
import * as uuid from 'uuid'
import { Schema$PickMatchInfo } from '@protocol/pick'
import { BettingFoldGrouped } from '@store/bettingHistoryStore'
import { COMMUNICATION_BLOCK_TYPE, GAME_BANNER_TYPE, GlobalStoreKey, NOTICE_REQUEST_TYPE } from '@v2/constants/common'
import StorageUtils from '@utils/storage-utils'
import { GAME_TYPE } from '@game-type/game-market-types'
import { Schema$CheerForTeam } from '@interface/cheer-for-team'
import { ProducerType } from '@game-type/betradar-type'
import { Schema$SportIssue, Schema$SportTalkPickUserInfo } from '@interface/sport-issue'
import { isAndroid, isIOS, isMobile } from 'react-device-detect'
import PopupUtils from '@utils/popup-utils'

export type ClubPortals = IClubPortals.Schema
export type HotMatch = IHotMatch.Schema
export type LiveScore = ILiveScore.Schema
export type Giftbox = IGiftbox.Schema
export type OpenGiftbox = IOpenGiftbox.Schema
export type Attendance = IAttendance.Schema
export type MyInfo = IMyInfo.Schema
export type CouponRegisterInfo = ICouponRegisterInfo.Schema

export interface ComponentBettingParams {
  club: Schema$ClubInfo
  typeOn: BET_TYPE_ON
  callType: BET_CALL_TYPE
  matches: Schema$ComponentBettingParamMatch[]
  betMoney: number
  betMoneyType: PROPERTY_ID
  pickId?: string
  changeRate?: ChangeRateData[]
  bettorBetId: string
  matchupCategory?: MATCHUP_CATEGORY
}

export interface ComponentCartItem {
  cartId: string
  matchId: number
  sportId: number
  selection: string
  outcomeMetaId: string // lsports 의 메타아웃컴 아이디. selection 은 실제 아웃컴의 아이디. lsports 는 아웃컴 아이디가 메타 아이디가 아니다.
  rate: number
  marketName: string
  marketType: string
  match: ComponentMatchTeamName
  isSpecial?: boolean
  specifiers?: { [id: string]: string }
  changeRate?: ChangeRateData
  selectedOutcome?: OutcomeTrack
  producerId?: ProducerType
  isLocked?: boolean
}

export interface ComponentMatchTeamName {
  homeTeamName: string,
  awayTeamName: string
}

export class ComponentStore extends StoreExt {
  @observable
  clubPortals: ClubPortals

  @observable
  hotMatch: HotMatch

  @observable
  liveScore: LiveScore

  @observable
  giftbox: Giftbox

  @observable
  openGiftbox: OpenGiftbox

  @observable
  attendance: Attendance

  @observable
  myInfo: MyInfo

  @observable
  couponRegisterInfos: CouponRegisterInfo[]

  @observable
  couponIdsVanillaText: string

  @observable
  isCouponInputViewCallFromCouponCheckView: boolean

  @observable
  isCouponLoading: boolean

  @observable
  matchupBanners: IGameBanners.Schema

  @observable
  publishedBanners: Schema$GameBanner[]

  @observable
  selectedGameBanner: Schema$GameBanner

  @observable
  matchupBottomBanner: Schema$GameBanner

  @observable
  matchupListBanners: Schema$GameBanner[] // 매치리스트의 띠배너 목록

  @observable
  giftBoxGameBanner: IGiftBoxGameBanners.Schema

  @observable
  maintenanceBanner: Schema$GameBanner

  @observable
  // TODO 테스트를 위해 추가한 코드입니다. [이슈링크](https://task.zempublic.co.kr/view.php?id=10365)
  chattingBanner = {
    id: '0',
    order: 0,
    type: 0,
    state: 1,
    desc: '',
    bannerImageUrl: '',
    contentsImageUrl: 'https://d1i7hydgfclv7m.cloudfront.net/data_center/chat/images/73591dc4-ea1d-477a-bf74-cff50917af65.webp',
    homeImageUrl: 'https://d1i7hydgfclv7m.cloudfront.net/data_center/chat/images/b0b7100d-7cd2-4458-af65-e8221b048724.webp',
    buttonImageUrl: '',
    matchupBottomImageUrl: '',
    clubEventPostEnabled: false,
    matchupBottomPostEnabled: false,
    maintenanceEnabled: false,
  }

  @observable
  bettingRules: IBettingRules.BettingRule[]

  @observable
  selectedBettingRule: IBettingRules.BettingRule

  @observable
  componentBettingParams: ComponentBettingParams

  @observable
  originalLossLimitAmount: number

  @observable
  lossLimitAmount: number

  @observable
  userLossMoney: number

  selfUsageRestrictionMonthPeriod: number

  @observable
  playBlockTime: Date

  @observable
  isBetting: boolean

  @observable
  cartItems: ComponentCartItem[] = []

  clubOption: Schema$ClubOption

  @observable
  betMoney: number

  @observable
  appVersion: string

  mobileAppVersion: string

  @observable
  friendInvitationInvitor: FriendInvitationInvitor

  @observable
  isPossibleInvitor: boolean

  @observable
  extraClubId: string

  @observable
  extraClubUnreadMessageCount: number

  messageSendingTimeoutId: number

  @observable
  noticeBadgeEnabled: boolean

  @observable
  noticeDetail: Notice

  @observable
  tipDetail: Notice

  @observable
  urgencyMessage: UrgencyMessage

  @observable
  forefrontPopups: ForefrontPopup[]

  @observable
  homeBanners: Schema$GameBanner[] = []

  @observable
  homeMainBanners: Schema$GameHomeMainBanner[] = []

  @observable
  bettingHistoryBanner: Schema$GameBanner

  @observable
  chatBanner: Schema$GameBanner

  @observable
  tips: Notice[]

  @observable
  matchupNotices: Notice[] = []

  @observable
  extraOddsItemDetails: Schema$ExtraOddsItemDefDetail[]

  @observable
  selectedUserExtraOddsItem: UserExtraOddsItem

  @observable
  isVisibleEtcAlertOptions: boolean

  @observable
  registrationSourceDefs: Schema$RegistrationSourceDef[] = []

  @observable
  isGoToFirstScrollOfSportTab = false

  @observable
  isGoToScrollTopOfMatchList = false

  @observable
  selectedUserCommunicationBlocks: COMMUNICATION_BLOCK_TYPE[] = []

  @observable
  communicationBlockUserInfos: Schema$CommunicationBlockUserInfo[] = []

  @observable
  communicationBlockHasMore = false

  getGiftBoxTimer = null

  @observable
  cheerForTeams: Schema$CheerForTeam[]

  // @observable
  // sportIssuePreviewList: Schema$SportIssue[]
  //
  // @observable
  // sportTalkPickUserInfos: Schema$SportTalkPickUserInfo[]

  fallbackHandler: (() => void) | null = null

  constructor() {
    super()
    window.addEventListener('message', this.handleMessage)
  }

  handleMessage = event => {
    const { data = {} } = event
    const { type, key, value = {} } = data
    if (type === 'nj') {
      if (key === 'njSetMobileAppVersion') {
        this.setMobileAppVersion(value.msg)
        this.updateAppVersion()
      }
    }
  }

  @action
  cleanup = () => {
    this.componentBettingParams = null
    this.clearCartItems()
  }

  @action
  clearCartItems = () => {
    this.cartItems = []
  }

  @action
  fetchHome = async () => {
    const { signedin } = authStore
    try {
      try {
        const res = await this.api.component.getClubPortals({})
        this.setClubPortals(res)
      } catch (err) {
        console.error(err)
      }
      try {
        const res = await this.api.component.getHotMatch({})
        this.setHotMatch(res)
      } catch (err) {
        console.error(err)
      }
      try {
        const res = await this.api.component.getLiveScore({})
        this.setLiveScore(res)
      } catch (err) {
        console.error(err)
      }
      if (signedin) {
        await this.getGiftBox({})
        await this.getMyInfo({})
      }
    } catch (err) {
      console.error(err)
    }
  }

  @action
  getGiftBox = async (params: IGiftbox.Params) => {
    try {
      clearTimeout(this.getGiftBoxTimer)
      this.getGiftBoxTimer = setTimeout(async () => {
        const res = await this.api.component.getGiftbox(params)
        this.setGiftbox(res)
      }, 500)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  fetchOpenGiftbox = async (params: IOpenGiftbox.Params) => {
    try {
      const res = await this.api.component.openGiftbox(params)
      this.setOpenGiftbox(res)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  receiveAttendance = async (params: IEventRewardInfo.Params) => {
    let result
    try {
      result = await this.api.component.receiveAttendance(params)
    } catch (err) {
      console.error(err)
    }
    return result
  }

  @action
  getAttendance = async () => {
    try {
      const res = await this.api.component.getAttendance({})
      this.setAttendance(res)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  getMyInfo = async (params: IMyInfo.Params) => {
    try {
      const res = await this.api.component.getMyInfo(params)
      this.setMyInfo(res)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setBetting = (isBet: boolean) => {
    this.isBetting = isBet
  }

  @action
  setClubPortals = (clubPortals: ClubPortals): ClubPortals => {
    this.clubPortals = clubPortals
    return clubPortals
  }

  @action
  setHotMatch = (hotMatch: HotMatch): HotMatch => {
    this.hotMatch = hotMatch
    return hotMatch
  }

  @action
  setLiveScore = (liveScore: LiveScore): LiveScore => {
    this.liveScore = liveScore
    return liveScore
  }

  @action
  setGiftbox = (giftbox: Giftbox): Giftbox => {
    this.giftbox = giftbox
    return giftbox
  }

  @action
  setOpenGiftbox = (openGiftbox: OpenGiftbox): OpenGiftbox => {
    this.openGiftbox = openGiftbox
    return openGiftbox
  }

  @action
  setAttendance = (attendance: Attendance): Attendance => {
    this.attendance = attendance
    return attendance
  }

  @action
  setMyInfo = (myInfo: MyInfo): MyInfo => {
    this.myInfo = myInfo
    return myInfo
  }

  @action
  setCouponIdsVanillaText = (vanillaText: string) => {
    this.couponIdsVanillaText = vanillaText
  }

  @action
  setCouponRegisterInfos = (couponRegisterInfos: CouponRegisterInfo[]): CouponRegisterInfo[] => {
    this.couponRegisterInfos = couponRegisterInfos
    return couponRegisterInfos
  }

  @action
  initCouponIdsVanillaText = () => {
    this.couponIdsVanillaText = ''
  }

  @action
  initCouponRegisterInfo = () => {
    this.couponRegisterInfos = []
  }

  @action
  initCouponIdsVanillaTextAndCouponRegisterInfo = () => {
    this.initCouponIdsVanillaText()
    this.initCouponRegisterInfo()
  }

  @action
  registerCoupons = async () => {
    const filteredCouponRegisterInfos = this.couponRegisterInfos.filter(info => info.couponId)
    const couponIds = filteredCouponRegisterInfos.map(info => info.couponId)
    const couponRegisterInfos = await this.api.component.registerCoupons({ couponIds })
    this.setCouponRegisterInfos(couponRegisterInfos)
  }

  @action
  validateCoupons = async () => {
    this.setCouponLoading(true)
    const filteredCouponRegisterInfos = this.couponRegisterInfos.filter(info => info.couponId)
    const couponIds = filteredCouponRegisterInfos.map(info => info.couponId)
    const couponRegisterInfos = await this.api.component.validateCoupons({ couponIds })
    this.setCouponRegisterInfos(couponRegisterInfos)
    this.setCouponLoading(false)
  }

  @action
  setCouponInputViewCallFromCouponCheckView = isCouponInputViewCallFromCouponCheckView => {
    this.isCouponInputViewCallFromCouponCheckView = isCouponInputViewCallFromCouponCheckView
  }

  @action
  setCouponLoading = isCouponLoading => {
    this.isCouponLoading = isCouponLoading
  }

  fetchMatchupBanners = async (clubId: string) => {
    try {
      if (this.matchupBanners && this.matchupBanners.clubId === clubId) {
        return
      }
      const res = await this.api.component.getMatchupBanners({ clubId })
      this.setMatchupBanners(res)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setMatchupBanners = (gameBanner: IGameBanners.Schema): IGameBanners.Schema => {
    _.remove(gameBanner.banners, banner =>
      !(banner.type === GAME_BANNER_TYPE.EVENT || banner.type === GAME_BANNER_TYPE.LIVE_BET_EVENT))
    this.matchupBanners = gameBanner
    return gameBanner
  }

  fetchPublishedBanner = async (): Promise<void> => {
    try {
      const response = await this.api.component.getPublishedBanners(null)
      this.setPublishedBanners(response.banners)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setPublishedBanners = (banners: Schema$GameBanner[]): void => {
    this.publishedBanners = banners
  }

  @action
  setSelectedGameBanner = (banner: Schema$GameBanner): void => {
    this.selectedGameBanner = banner
  }

  @action
  fetchGiftBoxGameBanner = async () => {
    try {
      const res = await this.api.component.getGiftBoxGameBanners({})
      this.setGiftBoxGameBanner(res)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setGiftBoxGameBanner = (gameBanner: IGiftBoxGameBanners.Schema): IGiftBoxGameBanners.Schema => {
    this.giftBoxGameBanner = gameBanner
    return gameBanner
  }

  @action
  fetchMaintenanceBanner = async () => {
    try {
      const response = await this.api.component.getMaintenanceBanner({})
      this.setMaintenanceBanner(response.banner)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setMaintenanceBanner = (banner: Schema$GameBanner) => {
    this.maintenanceBanner = banner
  }

  @action
  fetchBettingRules = async (params: IBettingRules.Params) => {
    try {
      const res = await this.api.component.getBettingRules(params)
      this.setBettingRules(res.bettingRules)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setBettingRules = (bettingRules: IBettingRules.BettingRule[]) => {
    this.bettingRules = bettingRules
  }

  @action
  setSelectedBettingRule = (bettingRule: IBettingRules.BettingRule) => {
    this.selectedBettingRule = bettingRule
  }

  @action
  setBetMoney = (betMoney: number, betMoneyType = PROPERTY_ID.MONEY): number => {
    if (this.componentBettingParams) {
      this.componentBettingParams.betMoney = betMoney
      this.componentBettingParams.betMoneyType = betMoneyType
    }
    return betMoney
  }

  joinClub = async (club): Promise<boolean> => {
    let joinSuccess = false

    const { id, ownerId } = club
    const result = await matchupStore.joinClub(id, ownerId, true)
    if (result && result.id === id) {
      if (result.isMember) {
        club.isMember = true
        // await clubStore.fetchSimpleClubs({})
        clubStore.updateSimpleClubsAfterJoin(result)
        if (club.id === matchupStore.club.id) {
          matchupStore.setClub(club)
        }
        joinSuccess = true
      } else if (result.memberState === CLUB_MEMBER_STATE.SEEK_MEMBER) {
        showToast(TOAST_TYPE.INFO, '클럽 가입이 신청 되었습니다. 승인 후 이용 가능합니다.')
      }
      clubStore.fetchSimpleClubs({})
    }
    return joinSuccess
  }

  @action
  validateClub = async (clubId: string) => {
    let isValid = false

    const { club } = await this.api.club.getClubInfo({ clubId })
    if (!club) {
      return isValid
    }

    const { name, state, isMember, clubJoinState, memberState, isExpelled } = club
    if (state !== CLUB_STATE.RUNNING) {
      globalStore.pushDialogOk({
        title: name, text: ['클럽의 운영이 중지되었습니다.'], className: 'small-size',
        callbackCloseDialog: () => {
          const representativeClubId = globalStore.findGlobalSetting(GlobalStoreKey.REPRESENTATIVE_CLUB)?.value ?? null
          matchupStore.changeClubId(representativeClubId || process.env.DEFAULT_CLUB_ID)
        },
      })
    } else if (!isMember) {
      if (isExpelled) {
        globalStore.pushDialogOk({
          title: name,
          text: ['가입이 거부된 클럽입니다.클럽 쪽지를 통해 문의하시기 바랍니다.'],
          className: 'small-size',
        })
      } else if (memberState === CLUB_MEMBER_STATE.SEEK_MEMBER) {
        globalStore.pushDialogOk({
          title: name,
          text: ['클럽 가입 승인 대기중입니다.'],
          className: 'small-size',
        })
      } else if (clubJoinState === CLUB_JOIN_STATE.DENY) {
        globalStore.pushDialogOk({ title: name, text: ['클럽 가입 거부 상태입니다.'], className: 'small-size' })
      } else if (this.componentBettingParams?.pickId ?? null) {
        globalStore.pushDialogYesNo({
          title: name,
          text: ['클럽 미가입 상태입니다.', '가입하시겠습니까?'],
          className: 'small-size',
          positivePress: '클럽무료가입',
          negativePress: '취소',
          callbackPositive: async () => {
            const success = await this.joinClub(club)
            if (success) {
              showToast(TOAST_TYPE.INFO, '클럽 가입이 완료 되었습니다.')
              isValid = true
            }
          },
        })
      } else {
        try {
          const success = await this.joinClub(club)
          if (success) {
            isValid = true
          }
        } catch (err) {
          showToast(TOAST_TYPE.ERROR, '클럽 가입 중 에러가 발생 했습니다.')
        }
      }
    } else {
      isValid = true
    }
    return isValid
  }

  @action
  setComponentBettingParams = (
    componentBettingParams: ComponentBettingParams, visibleComponentBetSlip?: boolean
  ): ComponentBettingParams => {
    this.componentBettingParams = componentBettingParams
    if (visibleComponentBetSlip) {
      globalStore.setVisibleComponentBetSlip(true)
    }
    return componentBettingParams
  }

  @action
  setCartItemsFromBettingFoldGrouped = (betFolds: BettingFoldGrouped[]) => {
    this.clearCartItems()
    betFolds.forEach(fold => {
      fold.data.forEach(outCome => {
        const matchTeamName = makeComponentMatchTeamName(fold.homeTeamName, fold.awayTeamName)
        const numberOutcomeRate = Number(outCome.outcomeRate)
        const selectedOutcome = makeSelectedOutcome(
          outCome.selection, numberOutcomeRate, outCome.selectionName, outCome.outcomeMetaId,
        )

        const cartItem: ComponentCartItem = {
          cartId: uuid.v4(),
          matchId: fold.matchId,
          sportId: fold.sportId,
          selection: outCome.selection,
          outcomeMetaId: outCome.outcomeMetaId,
          rate: numberOutcomeRate,
          marketName: outCome.marketName,
          marketType: outCome.marketType,
          match: matchTeamName,
          specifiers: outCome.specifiers ? JSON.parse(outCome.specifiers) : undefined,
          selectedOutcome,
        }
        this.addCartItem(cartItem)
      })
    })
  }

  @action
  addCartItemsFromPickMatches = (matches: Schema$PickMatchInfo[]) => {
    matches.forEach(match => {
      match.folds.forEach(fold => {
        const matchTeamName = makeComponentMatchTeamName(match.homeTeamName, match.awayTeamName)
        const selectedOutcome = makeSelectedOutcome(
          fold.outcome.type, fold.outcome.rate, fold.selectionName, fold.outcome.metaId,
        )
        const cartItem: ComponentCartItem = {
          cartId: uuid.v4(),
          matchId: match.matchId,
          sportId: match.sportId,
          selection: fold.outcome.type,
          outcomeMetaId: fold.outcome.metaId,
          rate: fold.outcome.rate,
          marketName: fold.marketName,
          marketType: fold.marketType,
          match: matchTeamName,
          specifiers: fold.specifiers ?? undefined,
          selectedOutcome,
        }

        this.addCartItem(cartItem)
      })
    })
  }

  @action
  addCartItem = (cartItem: ComponentCartItem) => {
    this.cartItems = this.cartItems.concat(cartItem)
  }

  refreshClubOption = async (clubId: string, betMoneyType: number) => {
    const { clubOption } = await this.api.club.getClubOption({ clubId, betMoneyType })
    this.clubOption = clubOption
  }

  @action
  bet = async () => {
    try {
      if (!this.componentBettingParams) {
        return
      }

      if (_.toNumber(this.componentBettingParams.betMoney) === 0) {
        showToast(TOAST_TYPE.INFO, '베팅머니를 입력하세요.')
        return
      }

      if (this.componentBettingParams.callType === BET_CALL_TYPE.SINGLE
        && !matchupStore.isEnabledSingleBet(this.componentBettingParams.betMoneyType)) {
        const errText = getErrorText(ERR.BET_FAIL_SINGLE_BET_WITH_ADMIN_CONFIG)
        globalStore.showBubblePopupTimer(BubbleTextType.DEFAULT_ALERT, errText, BubbleType.COMMON_BET_SLIP)
        return
      }

      this.setBetting(true)
      globalStore.pushDialogWait({ wrapClassName: 'betting-dialog-wait', className: 'none' }) /* 투명 레이어 */

      const pickId = this.componentBettingParams.pickId ?? null
      const { betMoney, betMoneyType, bettorBetId } = this.componentBettingParams

      const call: Schema$ComponentBettingCall[] = []
      for (const match of this.componentBettingParams.matches) {
        for (const fold of match.folds) {
          const obj: Schema$ComponentBettingCall = {
            matchId: match.matchId,
            marketType: fold.marketType,
            specifiers: fold.specifiers,
            outcome: {
              type: fold.outcome.type,
              rate: fold.outcome.rate,
              metaId: fold.outcome.metaId,
            },
          }
          call.push(obj)
        }
      }

      const clubInfo = this.componentBettingParams.club
      const typeOn = this.componentBettingParams.typeOn
      const callType = this.componentBettingParams.callType
      const matchupCategory = this.componentBettingParams.matchupCategory

      const result = await this.api.component.bet({
        pickId, betMoney, bettingCalls: call, betMoneyType, bettorBetId,
        clubId: clubInfo.id, typeOn, callType, matchupCategory, appVersion: this.appVersion,
        feedSupplier: matchupStore.feedSupplier, marketProvider: matchupStore.getMarketProvider(),
      })

      const { errcode, rateFailFolds, user } = result

      this.setBetting(false)
      globalStore.closeDialog()
      const messagePrefix = pickId ? '픽 ' : ''
      const errText = getErrorText(errcode) || `${messagePrefix}베팅을 할 수 없습니다. 새로고침을 해주세요.`
      let club_bet = {
        betmoney_min: 0,
        betmoney_max: 0,
      }
      if (errcode) {
        switch (errcode) {
          case ERR.BET_FAIL_DIFFRENT_RATE:
            globalStore.showBubblePopupTimer(
              BubbleTextType.DEFAULT_ALERT,
              getErrorText(ERR.BET_FAIL_DIFFRENT_RATE),
              BubbleType.COMMON_BET_SLIP
            )
            this.testChangeRate(rateFailFolds)
            break
          case ERR.BET_FAIL_LESSER_BETTING_MIN:
          case ERR.BET_FAIL_GREATER_BETTING_MAX:
            await this.refreshClubOption(this.componentBettingParams.club.id, betMoneyType)
            club_bet = getClubMinMaxBet(this.componentBettingParams.typeOn, betMoneyType, this.clubOption)
            globalStore.showBubblePopupTimer(
              BubbleTextType.DEFAULT_ALERT,
              getMixMaxBetError(errText, club_bet.betmoney_min, club_bet.betmoney_max),
              BubbleType.COMMON_BET_SLIP
            )
            break
          case ERR.BET_FAIL_INACTIVE_LIVE:
            showToast(TOAST_TYPE.INFO, '비활성화된 베팅 타입입니다.')
            break
          case ERR.BET_FAIL_GREATER_HIT_MAX:
            await this.refreshClubOption(this.componentBettingParams.club.id, betMoneyType)
            this.clubOption.hit_max = betMoney === PROPERTY_ID.NEW_BET_TICKET_G
              ? this.clubOption.hit_max / RATIO_TICKET : this.clubOption.hit_max
            globalStore.pushDialogOk({
              title: `${formatNumber(this.clubOption.hit_max)}${G_ICON}`, text: [errText], className: 'small-size'
            })
            break
          case ERR.BET_FAIL_GREATER_LIMIT_BETTING_RATE:
            await this.refreshClubOption(this.componentBettingParams.club.id, betMoneyType)
            globalStore.showBubblePopupTimer(
              BubbleTextType.DEFAULT_ALERT,
              `${this.clubOption.limit_betting_rate}배 이상 베팅하실 수 없습니다.`,
              BubbleType.COMMON_BET_SLIP
            )
            break
          case ERR.PROCESSING_LIVE_BET_EXIST:
            showToast(TOAST_TYPE.INFO, '이미 진행 중인 베팅이 있습니다. 잠시 후 다시 시도해 주세요.')
            break
          case ERR.BET_FAIL_INSUFFICIENT_MONEY: {
            const convertedErrText = getErrorTextByBetMoneyType(errText, betMoneyType)
            globalStore.showBubblePopupTimer(BubbleTextType.DEFAULT_ALERT, convertedErrText, BubbleType.COMMON_BET_SLIP)
            break
          }
          default:
            globalStore.showBubblePopupTimer(BubbleTextType.DEFAULT_ALERT, errText, BubbleType.COMMON_BET_SLIP)
            break
        }
      } else {
        // globalStore.showBetCompleteAniTimer()
        globalStore.showComponentBetCompleteAniTimer()
        // globalStore.showBubblePopupTimer(BubbleTextType.SUCCESS, SUCCESS_PRE_BET_TEXT, BubbleType.COMMON_BET_SLIP)
        // await bettingHistoryStore.fetchBettingTotalCount(false, false)
        authStore.updateUserAssets(user)
      }
    } catch (err) {
      this.setBetting(false)
      globalStore.closeDialog()
      console.error(err)
      const pickId = this.componentBettingParams.pickId ?? null
      if (pickId) {
        globalStore.pushDialogOk({ title: '알림', text: ['픽 베팅을 할 수 없습니다. 새로고침을 해주세요.'], className: 'small-size' })
      } else {
        globalStore.pushDialogOk({ title: '알림', text: ['베팅 실패! 잠시후 다시 시도해 주세요.'], className: 'small-size' })
      }

    }
  }

  @action
  clearChangeRate = () => {
    const componentBettingParams = {
      ...this.componentBettingParams,
      changeRate: undefined,
    }
    this.componentBettingParams = componentBettingParams
    return componentBettingParams
  }

  @action
  testChangeRate = (rateFailFolds: Schema$RateFailBetFold[]) => {
    for (const ratefailFold of rateFailFolds) {
      const matchFound = _.find(this.componentBettingParams.matches, { matchId: ratefailFold.matchId })
      const betfold = _.find(matchFound.folds, { marketType: ratefailFold.marketType })
      betfold.outcome.rate = Number(ratefailFold.serverRate)
      this.cartItemChangeRate(ratefailFold)
    }

    const componentBettingParams = {
      ...this.componentBettingParams,
    }
    this.setComponentBettingParams(componentBettingParams)
  }

  @action
  cartItemChangeRate = (rateFailFold: Schema$RateFailBetFold) => {
    const cartItem = _.find(this.cartItems, { matchId: rateFailFold.matchId, marketType: rateFailFold.marketType })
    if (cartItem) {
      cartItem.rate = Number(rateFailFold.serverRate)
      const changeRateData: ChangeRateData = {
        homeTeamName: cartItem.match.homeTeamName,
        awayTeamName: cartItem.match.awayTeamName,
        marketType: rateFailFold.marketType,
        from: {
          type: cartItem.selectedOutcome.type,
          rate: Number(rateFailFold.clientRate),
          metaId: cartItem.selectedOutcome.metaId,
        },
        to: {
          type: cartItem.selectedOutcome.type,
          rate: Number(rateFailFold.serverRate),
          metaId: cartItem.selectedOutcome.metaId,
        },
      }

      cartItem.changeRate = changeRateData
    }
  }

  fetchLossLimitAmountAndUserLossMoney = async () => {
    try {
      const result = await this.api.component.fetchLossLimitAmountAndUserLossMoney({})
      const { lossLimitAmount, userLossMoney } = result
      this.setOriginalLossLimitAmount(lossLimitAmount)
      this.setLossLimitAmount(lossLimitAmount)
      this.setUserLossMoney(userLossMoney)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setOriginalLossLimitAmount = (amount: number) => {
    this.originalLossLimitAmount = amount
  }

  @action
  setUserLossMoney = (money: number) => {
    this.userLossMoney = money
  }

  saveLossLimitAmount = async () => {
    try {
      const result = await this.api.component.saveLossLimitAmount({ amount: this.lossLimitAmount })
      return result
    } catch (err) {
      console.error(err)
    }
    return null
  }

  saveSelfUsageRestriction = async () => {
    try {
      const result = await this.api.component.saveSelfUsageRestriction({
        monthPeriod: this.selfUsageRestrictionMonthPeriod
      })
      return result
    } catch (err) {
      console.error(err)
    }
    return null
  }

  @action
  updateAppVersion = () => {
    let version = process.env.APP_VERSION
    if (!_.isEmpty(this.mobileAppVersion)) {
      version += ` (APP.${this.mobileAppVersion})`
    }
    this.appVersion = version
  }

  getAppVersion = () => {
    this.updateAppVersion()
    return this.appVersion
  }

  @action
  setMobileAppVersion = (version: string) => {
    this.mobileAppVersion = version
  }

  @action
  setLossLimitAmount = (amount: number) => {
    this.lossLimitAmount = amount
  }

  setSelfUsageRestrictionMonthPeriod = (selfUsageRestrictionMonthPeriod: number) => {
    this.selfUsageRestrictionMonthPeriod = selfUsageRestrictionMonthPeriod
  }

  fetchFriendInvitationInvitor = async (targetAt: Date, betMoneyType: BET_MONEY_TYPE) => {
    try {
      const result = await this.api.component.getFriendInvitationInvitor({ targetAt, betMoneyType })
      this.setFriendInvitationInvitor(result.invitor)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setFriendInvitationInvitor = (invitor: FriendInvitationInvitor) => {
    this.friendInvitationInvitor = invitor
  }

  fetchIsPossibleInvitor = async () => {
    try {
      const result = await this.api.component.isPossibleToInvitor(null)
      this.setIsPossibleInvitor(result.isPossibleInvitor)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setIsPossibleInvitor = (isPossible: boolean) => {
    this.isPossibleInvitor = isPossible
  }

  @action
  setExtraClubId = (clubId: string) => {
    this.extraClubId = clubId
  }

  @action
  setExtraClubUnreadMessageCount = (count: number) => {
    this.extraClubUnreadMessageCount = count
  }

  setMessageSendingTimeoutId = (id: number) => {
    this.messageSendingTimeoutId = id
  }

  fetchNoticeBadgeEnabled = async () => {
    try {
      const result = await this.api.component.fetchNoticeBadgeEnabled(null)
      this.setNoticeBadgeEnabled(result.enabled)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setNoticeBadgeEnabled = (enabled: boolean) => {
    this.noticeBadgeEnabled = enabled
  }

  @action
  setNoticeDetail = (noticeDetail: Notice) => {
    this.noticeDetail = noticeDetail
  }

  @action
  setTipDetail = (tipDetail: Notice) => {
    this.tipDetail = tipDetail
  }

  @action
  setUrgencyMessage = (urgencyMessage: UrgencyMessage) => {
    this.urgencyMessage = urgencyMessage
  }

  checkAppVersion = async () => {
    try {
      const result = await this.api.component.checkAppVersion({ appVersion: process.env.APP_VERSION })
      if (!result.isValidVersion) {
        window.location.reload()
      }
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setForefrontPopups = (forefrontPopups: ForefrontPopup[]) => {
    this.forefrontPopups = forefrontPopups
  }

  @action
  removeForefrontPoupById = (id: string) => {
    const popups = _.remove(this.forefrontPopups, (forefrontPopup) => {
      return forefrontPopup.id !== id
    })
    this.setForefrontPopups(popups)
  }

  @action
  fetchForefrontPopups = async (): Promise<boolean> => {
    try {
      const showHistory = StorageUtils.getForeFrontPopupList()

      const result = await this.api.component.getForefrontPopups({ showHistory })
      const { rows } = result

      this.setForefrontPopups(rows)
      return !_.isEmpty(this.forefrontPopups)
    } catch (err) {
      console.error(err)
      return false
    }
  }

  fetchHomeBanners = async (): Promise<Schema$GameBanner[]> => {
    try {
      const response = await this.api.component.fetchHomeBanners(null)
      this.setHomeBanners(response.banners)
      return response.banners
    } catch (err) {
      console.error(err)
    }
  }

  fetchHomeMainBanners = async (): Promise<Schema$GameHomeMainBanner[]> => {
    try {
      const response = await this.api.component.fetchHomeMainBanners(null)
      this.setHomeMainBanners(response.banners)
      return response.banners
    } catch (err) {
      console.error(err)
    }
  }

  fetchMatchupListBanners = async (): Promise<Schema$GameBanner[]> => {
    try {
      const response = await this.api.component.fetchMatchupListBanners(null)
      this.setMatchupListBanners(response.banners)
      return response.banners
    } catch (err) {
      console.error(err)
    }
  }

  fetchTips = async (): Promise<void> => {
    try {
      const response = await this.api.component.fetchTips(null)
      this.setTips(response.rows)
    } catch (err) {
      console.error(err)
    }
  }

  fetchBettingHistoryBanners = async (): Promise<void> => {
    try {
      const response = await this.api.component.fetchBettingHistoryBanner()
      this.setBettingHistoryBanner(response.banner)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setBettingHistoryBanner(banner: Schema$GameBanner) {
    this.bettingHistoryBanner = banner
  }

  fetchChatBanners = async (): Promise<void> => {
    try {
      const response = await this.api.component.fetchChatBanner()
      this.setChatBanner(response.banner)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setChatBanner(banner: Schema$GameBanner) {
    this.chatBanner = banner
  }

  @action
  setHomeBanners = (banners: Schema$GameBanner[]): void => {
    this.homeBanners = banners
  }

  @action
  setHomeMainBanners = (banners: Schema$GameHomeMainBanner[]): void => {
    this.homeMainBanners = banners
  }

  @action
  setMatchupListBanners = (banners: Schema$GameBanner[]): void => {
    this.matchupListBanners = banners
  }

  @action
  fetchMatchupBottomBanner = async (): Promise<Schema$GameBanner | undefined> => {
    try {
      const response = await this.api.component.fetchMatchupBottomBanner()
      this.setMatchupBottomBanner(response.banner)
      return response.banner
    } catch (error) {
      console.error(error)
    }
    return undefined
  }

  @action
  setMatchupBottomBanner = (banner: Schema$GameBanner): void => {
    this.matchupBottomBanner = banner
  }

  /**
   * 어드민에 등록된 오늘의 승부사 메인 매치업 배너 스케쥴을 체크 하여 강제로 띄워 준다.
   *
   * 10초 마다 체크
   */
  @action
  isShowAvailableMatchupBottomBanner(): boolean {
    try {
      let nextOpenTime = StorageUtils.getMatchupBottomBannerShowNext7Days()
      if (nextOpenTime > new Date().getTime()) {
        if (globalStore.visibleMatchupBottomBannerPopup) {
          globalStore.setVisibleMatchupBottomBannerPopup(false)
        }
        return false
      }

      nextOpenTime = StorageUtils.getMatchupBottomBannerShowNextTime()
      if (nextOpenTime > new Date().getTime()) {
        if (globalStore.visibleMatchupBottomBannerPopup) {
          globalStore.setVisibleMatchupBottomBannerPopup(false)
        }
        return false
      }

      StorageUtils.removeMatchupBottomBannerHide()
      return true
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setTips = (tips: Notice[]): void => {
    this.tips = tips
  }

  createNoticeReadHistory = async (data: INoticeCreate.Params): Promise<INoticeCreate.Schema> => {
    return this.api.component.createNoticeReadHistory(data)
  }

  createAllNoticeReadHistory = async (data: INoticeCreate.Params): Promise<INoticeCreate.Schema> => {
    return this.api.component.createAllNoticeReadHistory(data)
  }

  fetchNotices = async (params: INotices.Params): Promise<INotices.Schema> => {
    return this.api.component.getNotices(params)
  }

  fetchNotice = async (params: INotice.Params): Promise<INotice.Schema> => {
    return this.api.component.getNotice(params)
  }

  /**
   * 매치업 상단 게시된 공지사항을 가지고 온다.
   * 읽은 공지사항은 제외 하고 가지고 온다.
   */
  @action
  fetchMatchupNotices = async (): Promise<void> => {
    try {
      const res = await this.api.component.getNotices({
        offset: 0, limit: 20, type: NOTICE_REQUEST_TYPE.MATCH_UP,
      })
      if (res) {
        runInAction(() => {
          this.matchupNotices = res?.rows || []
        })
      }
    } catch (err) {
      console.error(err)
    }
  }

  @action
  updateMatchupNotice = (notice: Notice): void => {
    if (!notice.isVisibleMatchup) {
      _.remove(this.matchupNotices, { id: notice.id })
    } else if (_.size(this.matchupNotices) === 0) {
      this.matchupNotices.push(notice)
    } else {
      const existsNoticeIdx = _.findIndex(this.matchupNotices, { id: notice.id })
      if (_.isNil(existsNoticeIdx) || existsNoticeIdx < 0) {
        const minResult: Notice[] = []
        const maxResult: Notice[] = []
        for (const matchupNotice of this.matchupNotices) {
          if (matchupNotice.id < Number(notice.id)) {
            minResult.push(matchupNotice)
          } else {
            maxResult.push(matchupNotice)
          }
        }
        this.matchupNotices = [...minResult, notice, ...maxResult]
      } else {
        this.matchupNotices[existsNoticeIdx] = { ...notice }
      }
    }
  }

  documentVisibleChangeHandler = (isHidden: boolean): void => {
    if (isHidden) {
      return
    }
    if (authStore.isSignedIn) {
      this.fetchMatchupNotices().catch(console.error)
    }
  }

  fetchExtraOddsItemDetails = async () => {
    try {
      const result = await this.api.component.fetchExtraOddsItemDetails(null)
      this.setExtraOddsItemDetails(result.details)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setExtraOddsItemDetails = (defs: Schema$ExtraOddsItemDefDetail[]): void => {
    this.extraOddsItemDetails = defs
  }

  @action
  setSelectedUserExtraOddsItem = (item: UserExtraOddsItem): void => {
    this.selectedUserExtraOddsItem = item
    if (!_.isEmpty(item)) {
      StorageUtils.saveLastSelectedGodBlessItemPropertyId(item.propertyId)
    }
  }

  makeUserExtraOddsItems = (): UserExtraOddsItem[] => {
    const userExtraOddsItems = authStore.getUserExtraOddsItems()
    return _.chain(this.extraOddsItemDetails)
      .filter(item => userExtraOddsItems?.[item.propertyId] > 0)
      .sort((a, b) => b.rate - a.rate)
      .map(def => _.extend(def, { key: def.propertyId }))
      .value()
  }

  makeUserExtraOddsItemByPropertyId = (propertyId: number): UserExtraOddsItem => {
    const count = this.getUserExtraOddsItemCountByPropertyId(propertyId)
    if (count < 1) {
      return null
    }

    const def = this.getExtraOddsItemDetailByPropertyId(propertyId)
    if (_.isEmpty(def)) {
      return null
    }
    return _.extend(def, { key: def.propertyId })
  }

  getNextAutoSelectionUserExtraOddsItem = () => {
    const items = this.makeUserExtraOddsItems()
    if (_.isEmpty(items)) {
      return null
    }
    return items[0]
  }

  @action
  updateSelectedUserExtraOddsItemAfterAddCart = (): void => {
    const isAutoSelectionEnabled = this.isGodBlessItemAutoSelectionEnabledByClubMembership()
    if (!isAutoSelectionEnabled) {
      return
    }

    const { cart } = matchupStore
    if (_.size(cart) !== 1) {
      return
    }

    const propertyId = StorageUtils.getLastSelectionGodBlessItemPropertyId()
    let item = this.makeUserExtraOddsItemByPropertyId(_.toNumber(propertyId))
    if (_.isEmpty(item)) {
      item = this.getNextAutoSelectionUserExtraOddsItem()
    }
    this.setSelectedUserExtraOddsItem(item)
  }

  @action
  updateSelectedUserExtraOddsItemAfterBet = (): void => {
    const isAutoSelectionEnabled = this.isGodBlessItemAutoSelectionEnabledByClubMembership()
    const { isKeepCart } = matchupStore

    // 슬립 유지 ON
    if (isKeepCart) {
      // 선택된 신의가호가 없으면 자동 갱신을 하지 않는다.
      if (_.isEmpty(this.selectedUserExtraOddsItem)) {
        return
      }

      // 선택된 신의가호가 0개 이상인 경우 현재 상태를 유지 한다.
      const userExtraOddsItems = authStore.getUserExtraOddsItems()
      const count = userExtraOddsItems[this.selectedUserExtraOddsItem?.propertyId] ?? 0
      if (count > 0) {
        return
      }

      // 자동선택 ON, 선택된 신의가호 0개: 다음 신의 가호 아이템이 선택된다.
      if (isAutoSelectionEnabled) {
        const item = this.getNextAutoSelectionUserExtraOddsItem()
        this.setSelectedUserExtraOddsItem(item)
        return
      }
    }

    this.setSelectedUserExtraOddsItem(null)
  }

  isGodBlessItemAutoSelectionEnabledByClubMembership = (): boolean => {
    const { club } = matchupStore
    if (club?.grade === CLUB_MEMBERSHIP_ID.FREE) {
      return StorageUtils.isGodBlessItemAutoSelectionFreeClubEnabled()
    } else {
      return StorageUtils.isGodBlessItemAutoSelectionMemberShipClubEnabled()
    }
  }

  getUserExtraOddsItemCountByPropertyId = (propertyId: EXTRA_ODDS_ITEM_PROPERTY): number => {
    const items = authStore.getUserExtraOddsItems()
    return items?.[propertyId] ?? 0
  }

  getUserExtraOddsItemTotalCount = (): number => {
    const items = authStore.getUserExtraOddsItems()
    let totalCount = 0
    const counts = _.values(items)
    _.forEach(counts, count => {
      totalCount += count
    })
    return totalCount
  }

  getExtraOddsItemDetailByPropertyId = (propertyId: EXTRA_ODDS_ITEM_PROPERTY): Schema$ExtraOddsItemDefDetail => {
    return _.find(this.extraOddsItemDetails, def => def.propertyId === propertyId)
  }

  getExtraOddsItemDetailByItemId = (itemId: number): Schema$ExtraOddsItemDefDetail => {
    return _.find(this.extraOddsItemDetails, def => def.itemId === itemId)
  }

  @action
  updateIsVisibleEtcAlertOptions = () => {
    const { currentGameKey } = matchupStore
    if (currentGameKey !== GAME_TYPE.CROSS) {
      this.isVisibleEtcAlertOptions = false
      return
    }

    const options = globalStore.getMatchupEtcFilterAlertOptions()
    const { enabled, updatedAt, imageUrlPrefix, imageFileName } = options

    if (!enabled) {
      this.isVisibleEtcAlertOptions = false
      return
    }

    if (_.isEmpty(imageUrlPrefix)) {
      this.isVisibleEtcAlertOptions = false
      return
    }

    if (_.isEmpty(imageFileName)) {
      this.isVisibleEtcAlertOptions = false
      return
    }

    const { club } = matchupStore
    if (_.isEmpty(club) || club.grade === CLUB_MEMBERSHIP_ID.FREE) {
      this.isVisibleEtcAlertOptions = false
      return
    }

    const beforeUpdatedAt = StorageUtils.getMatchEtcFilterAlertUpdatedTime()
    if (_.isNil(beforeUpdatedAt)) {
      this.isVisibleEtcAlertOptions = true
      return
    }

    if (beforeUpdatedAt < updatedAt) {
      this.isVisibleEtcAlertOptions = true
      return
    }

    this.isVisibleEtcAlertOptions = false
  }

  @action
  setRegistrationSourceDefs(defs: Schema$RegistrationSourceDef[]) {
    this.registrationSourceDefs = defs
  }

  fetchRegistrationSourceDefs = async (): Promise<void> => {
    try {
      const result = await this.api.component.fetchRegistrationSourceDefs({})
      this.setRegistrationSourceDefs(result.defs)
    } catch(err) {
      console.error(err)
    }
  }

  @action
  setIsGoToFirstScrollOfSportTab(isGoToFirstScroll: boolean): void {
    this.isGoToFirstScrollOfSportTab = isGoToFirstScroll
  }

  @action
  setIsGoToScrollTopOfMatchList(isGoToScrollTop: boolean): void {
    this.isGoToScrollTopOfMatchList = isGoToScrollTop
  }

  @action
  resetSelectedUserCommunicationBlocks() {
    this.selectedUserCommunicationBlocks = []
  }

  @action
  setSelectedUserCommunicationBlocks(
    communicationBlockTypes: COMMUNICATION_BLOCK_TYPE[]
  ): void {
    this.selectedUserCommunicationBlocks = _.concat([], communicationBlockTypes)
  }

  @action
  resetCommunicationBlockUserInfos() {
    this.communicationBlockUserInfos = []
    this.communicationBlockHasMore = false
  }

  @action
  setCommunicationBlockUserInfos(
    newBlockUserInfos: Schema$CommunicationBlockUserInfo[]
  ): void {
    this.communicationBlockUserInfos = _.concat([], newBlockUserInfos)
  }

  @action
  setCommunicationBlockUserInfosHasMore(
    hasMore: boolean
  ): void {
    this.communicationBlockHasMore = hasMore
  }

  @action
  removeCommunicationBlockUserInfo(userPromiseId: string): void {
    const blockUserInfos = _.cloneDeep(this.communicationBlockUserInfos)
    _.remove(blockUserInfos, userInfo => userInfo.userPromiseId === userPromiseId)
    this.setCommunicationBlockUserInfos(blockUserInfos)
  }

  @action
  async fetchCommunicationBlockUserInfos(reset: boolean): Promise<void> {
    const offset = _.size(this.communicationBlockUserInfos)

    if (reset) {
      this.resetCommunicationBlockUserInfos()
    }

    const { blockUserInfos, hasMore } = await this.api.component.fetchCommunicationBlockUserInfos({ offset })
    this.setCommunicationBlockUserInfosHasMore(hasMore)

    if (blockUserInfos) {
      const newBlockUserInfos = _.concat(this.communicationBlockUserInfos, blockUserInfos)
      this.setCommunicationBlockUserInfos(newBlockUserInfos)
    }
  }

  @action
  async fetchUserCommunicationBlockInfo(userPromiseId: string) {
    try {
      const result = await this.api.component.fetchUserCommunicationBlockInfo({ userPromiseId })

      let communicationBlockInfos = result.communicationBlockInfo
      if (_.isEmpty(communicationBlockInfos)) {
        const blockTypes = _.chain(COMMUNICATION_BLOCK_TYPE)
          .filter((value) => _.isNumber(value))
          .value()

        communicationBlockInfos = _.concat([], blockTypes)
      }

      this.setSelectedUserCommunicationBlocks(communicationBlockInfos)
    } catch (err) {
      console.error(err)
    }
  }

  async setRegistrationCommunicationBlock(
    userPromiseId: string,
  ): Promise<IRegistrationCommunicationBlock.Schema> {
    try {
      const communicationBlockInfo = this.selectedUserCommunicationBlocks

      if (_.isEmpty(communicationBlockInfo)) {
        return null
      }

      return this.api.component.setRegistrationCommunicationBlock({
        userPromiseId,
        communicationBlockInfo,
      })
    } catch (err) {
      console.error(err)
      return null
    }
  }

  @action
  async unblockCommunicationBlockUser(
    userPromiseId: string,
  ): Promise<boolean> {
    try {
      const result = await this.api.component.unblockCommunicationBlockUser({ userPromiseId })

      if (!result) {
        return false
      }

      return result.ok
    } catch (err) {
      console.error(err)
      return false
    }
  }

  setGameHomeMainBanner(banners: Schema$GameHomeMainBanner[]): void {
    this.homeMainBanners = banners
  }

  @action
  updateGameHomeMainBanner(newBanner: Schema$GameHomeMainBanner): void {
    const found = _.find(this.homeMainBanners, banner => banner.id === newBanner.id)
    if (found) {
      found.order = newBanner.order
      found.desc = newBanner.desc
      found.state = newBanner.state
      found.mobileImageUrl = newBanner.mobileImageUrl
      found.mobileImage = newBanner.mobileImage
      found.pcImage = newBanner.pcImage
      found.pcImageUrl = newBanner.pcImageUrl
    } else {
      this.homeMainBanners.push(newBanner)
    }
    this.setGameHomeMainBanner(_.sortBy(this.homeMainBanners, ['order']))
  }

  @action
  deleteGameHomeMainBanner(id: string): void {
    const found = _.filter(this.homeMainBanners, banner => banner.id !== id)
    this.setGameHomeMainBanner(found)
  }

  async fetchSportDashboardList(params: {sportId: number, matchId: number}): Promise<{
    issues: Schema$SportIssue[],
    pickWriterInfos: Schema$SportTalkPickUserInfo[]
  }> {
    const { sportId, matchId } = params
    if (_.isNil(matchId)) {
      return
    }

    return this.api.component.fetchSportDashboardList({ sportId, matchId })
  }

  async fetchSportIssueList(matchId: number, offset: number, limit: number): Promise<ISportIssueList.Schema> {
    return this.api.component.fetchSportIssueList({ matchId, offset, limit })
  }

  deepLinkingChampPokerByAndroid() {
    const options = globalStore.getAndroidChampPokerDeepLink()
    this.deepLinkingChampPoker(options, isAndroid)
  }

  deepLinkingChampPokerByIOS() {
    const options = globalStore.getIosChampPokerDeepLink()
    this.deepLinkingChampPoker(options, isIOS)
  }

  deepLinkingChampPoker(options: ChampPokerDeepLink, isScheme = false) {
    if (!options) {
      return
    }

    const { storeUrl, storeScheme, appScheme } = options

    if (isMobile && isScheme) {
      if (!_.isEmpty(_.trim(appScheme))) {
        window.location.href = appScheme
      }

      if (!_.isEmpty(_.trim(storeScheme))) {
        setTimeout(() => {
          if (document.hidden) {
            return
          }

          window.location.href = storeScheme
          this.setupFallbackForScheme(storeUrl)
        }, 1000)
      }
    } else if (isMobile && !isScheme) {
      const { storeUrl, storeScheme } = options

      const hasStoreUrl = !_.isEmpty(_.trim(storeUrl))
      const hasStoreScheme = !_.isEmpty(_.trim(storeScheme))

      if (!hasStoreUrl || (hasStoreUrl && !hasStoreScheme)) {
        return
      }

      window.location.href = storeUrl
    } else if (!_.isEmpty(_.trim(storeUrl))) {
      window.location.href = storeUrl
    }
  }

  clearFallbackForScheme() {
    if (this.fallbackHandler) {
      document.removeEventListener('visibilitychange', this.fallbackHandler)
      this.fallbackHandler = null
    }
  }

  setupFallbackForScheme(storeUrl: string) {
    this.clearFallbackForScheme()

    this.fallbackHandler = () => {
      if (!document.hidden) {
        window.location.href = storeUrl
      }
      this.clearFallbackForScheme()
    }
    document.addEventListener('visibilitychange', this.fallbackHandler)
  }

  @action
  fetchRequireLossLimitPopupTarget = async (): Promise<boolean> => {
    try {
      const result = await this.api.component.fetchRequireLossLimitPopupTarget()
      return result.isTarget
    } catch (err) {
      console.error(err)
      return false
    }
  }

  checkRequireLossLimitPopupTarget = async (): Promise<boolean> => {
    const setting = JSON.parse(globalStore.findGlobalSetting(GlobalStoreKey.REQUIRED_LOSS_LIMIT_SETTING_POPUP).value)
    if (!setting.enabled) {
      return false
    }
    return this.fetchRequireLossLimitPopupTarget()
  }

  showRequireLossLimitPopup = async (onClose?: () => void): Promise<boolean> => {
    const isTarget = await this.checkRequireLossLimitPopupTarget()
    if (isTarget) {
      PopupUtils.showLossLimitSettingPopup(onClose)
      return true
    }
    return false
  }
}

export default new ComponentStore()
